import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { GridColDef, GridActionsCellItem, GridRowParams, GridInitialState, GridToolbar, useGridApiRef } from '@mui/x-data-grid-pro';
import { ResponsiveDataGrid } from 'ui-component/ResponsiveDataGrid';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import { useUsers } from '../../../hooks/useUsers.hooks';
import { useUser } from '../../../hooks/useUser.hooks';
import { useCurrentUser } from '../../../hooks/useUser.hooks';

type SuperAdmin = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

const GRID_STATE_KEY = 'superAdminUsersDataGridState';

const UsersDataGrid = (props: any) => {
  const { active } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const usersState = useUsers('Super Admin', active);
  const userState = useUser();
  const currentUserState = useCurrentUser();
  const navigate = useNavigate();

  const [rows, setRows] = useState<SuperAdmin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [activeUserID, setActiveUserID] = useState(-1);

  const apiRef = useGridApiRef();
  const [initialState, setInitialState] = useState<GridInitialState>();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(GRID_STATE_KEY, JSON.stringify(currentState));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(GRID_STATE_KEY);
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  let usersArray : SuperAdmin[];
  useEffect(() => {
    if (usersState?.ready && !userState.deleteInProgress) {
      usersArray = Object.values(usersState.users).sort((a : any, b : any) => a.first_name.localeCompare(b.first_name));
      setRows(usersArray);
      setLoading(false);
    }
  }, [usersState?.ready, userState?.deleteInProgress, usersState.users]);

  const handleEdit = (id: number) => {
    navigate(`/super-admin/users/${id}/edit`);
  };

  const cancelDialog = () => {
    setShowArchiveConfirmation(false);
    setShowReactivateConfirmation(false);
  }

  const confirmArchive = async () => {
    await userState.deleteUser(activeUserID, true);
    await usersState.fetch(); // to refresh users
    dispatch(
      openSnackbar({
          open: true,
          message: 'Super Admin account successfully archived',
          variant: 'alert',
          close: false,
          alert: {
            color: 'success'
          },
      })
    );
    setShowArchiveConfirmation(false);
  };

  const confirmReactivate = async () => {
    await userState.reactivateUser(activeUserID, true);
    await usersState.fetch(); // to refresh users
    dispatch(
      openSnackbar({
          open: true,
          message: 'Super Admin account successfully reactivated',
          variant: 'alert',
          close: false,
          alert: {
            color: 'success'
          },
      })
    );
    setShowReactivateConfirmation(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => {
        if (active) {
          const isCurrentUser = currentUserState.ready && currentUserState.user.id === params.row.id;
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              }
              label="Edit"
              className="textPrimary"
              onClick={() => handleEdit(params.row.id)}
              color="inherit"
            />,
            <Tooltip
              title={isCurrentUser ? "You cannot archive yourself" : ""}
              arrow
            >
              <span>
                <GridActionsCellItem
                  icon={
                    <Tooltip title="Archive">
                      <ArchiveOutlinedIcon />
                    </Tooltip>
                  }
                  label="Archive"
                  onClick={() => {
                    setActiveUserID(params.row.id as number);
                    setShowArchiveConfirmation(true);
                  }}
                  disabled={isCurrentUser}
                  color="inherit"
                />
              </span>
            </Tooltip>,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Reactivate">
                <UnarchiveOutlinedIcon />
              </Tooltip>
            }
            label="Reactivate"
            onClick={() => {
              setActiveUserID(params.row.id as number);
              setShowReactivateConfirmation(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: 'first_name', headerName: 'First Name', width: 160 },
    { field: 'last_name', headerName: 'Last Name', width: 160 },
    { field: 'email', headerName: 'Email', width: 300 },
    { field: 'id', headerName: 'ID', width: 120 },
  ];

  if (!usersState?.ready) {
    return (
      <div>Loading...</div>
    )
  }

  if (!initialState) {
    return <CircularProgress />;
  }

  return (
    <div style={{ height: 700, width: '100%' }}>
      <Box
        sx={{
          height: 700,
          width: '100%',
          '& .actions': {
            color: 'text.secondary',
          },
          '& .textPrimary': {
            color: 'text.primary',
          },
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnsContainer': {
              color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
              borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnSeparator': {
              color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            }
          },
          '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: `1px solid ${theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'}`
          }
        }}
      >
        <ResponsiveDataGrid
          rows={rows}
          columns={columns}
          pagination
          slots={{ toolbar: GridToolbar }}
          apiRef={apiRef}
          initialState={{
            ...initialState,
            sorting: {
              sortModel: [{ field: 'first_name', sort: 'asc' }]
            }
          }}
          loading={loading}
          disableRowSelectionOnClick
        />
        <ConfirmationDialog
            open={showArchiveConfirmation}
            onCancel={cancelDialog}
            onConfirm={confirmArchive}
            title="Archive User?"
            message="Confirm you want to archive this user?"
        />
        <ConfirmationDialog
            open={showReactivateConfirmation}
            onCancel={cancelDialog}
            onConfirm={confirmReactivate}
            title="Reactivate User?"
            message="Confirm you want to reactivate this user?"
        />
      </Box>
      {!loading && rows.length === 0 && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          No Super Admin users found.
        </Typography>
      )}
      {usersState?.error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {usersState.error}
        </Typography>
      )}
    </div>
  );
};

export default UsersDataGrid;
